import { blockedQs, EXTRA_BLOCKED_QS, frameworkBlockedQs } from '~/blockedQs'

// DECO-5819: os links de paginação/ordenação repetiam a query string entrante
// inteira, vazando PII (?email=, ?name=) e tracking (gclid, utm_*) em todo href.
//
// Em produção quem filtra é o framework: `unstable_blockUseSectionHrefQueryStrings`
// injeta a lista da loja no `BLOCKED_QS` do @deco/deco, aplicado em
// `createStableHref` (href do `useSection`). Aqui esse caminho não existe — o
// `useSection` virou stub —, então aplicamos a mesma lista na montagem dos links.
// Denylist, e não allowlist: parâmetro funcional não enumerado (`map`, `O`, `q`,
// `filter.*`, `idsku`, `fresh-partial`…) continua passando, igual em produção.
const BLOCKED_QS = new Set(
    [...frameworkBlockedQs, ...blockedQs, ...EXTRA_BLOCKED_QS].map((qs) => qs.toLowerCase()),
)

// Prefixo: o original enumera utm_* uma a uma e não cobre variantes novas
// (utm_ad, utm_placement…). Nenhum utm_* é funcional, então bloquear a família
// inteira é superset seguro.
const BLOCKED_PREFIXES = ['utm_']

// A comparação do framework é case-sensitive; aqui normalizamos porque a query
// chega com casing variado na prática (`Email`, `UTM_Source`). É superset da
// lista original e nenhum parâmetro funcional colide.
const isBlocked = (key: string) => {
    const k = key.toLowerCase()

    return BLOCKED_QS.has(k) || BLOCKED_PREFIXES.some((prefix) => k.startsWith(prefix))
}

/**
 * Remove os parâmetros bloqueados de `url` e ordena o que sobrou, in place —
 * mesma sequência do `createStableHref` do framework (delete + `searchParams.sort()`).
 *
 * A ordenação não é cosmética: além de estabilizar a chave de cache entre
 * ordens diferentes da mesma query, é o que mantém a canonical limpa (ver
 * `getCanonicalByURL` em components/seo/PageSEO.tsx, que fatia a query por
 * `split('page=')` e herda a cauda inteira).
 */
export default function stripTrackingParams<T extends URL | URLSearchParams>(url: T): T {
    const params = url instanceof URLSearchParams ? url : url.searchParams

    for (const key of [...params.keys()]) {
        if (isBlocked(key)) params.delete(key)
    }

    params.sort()

    return url
}
